import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { SurveyAssignmentDto } from '@/shared/api/types'
import { recordAttempt } from './api'
import { CODE_LIST_UNAVAILABLE, useCodes } from './useVocabulary'
import { DateTimePicker } from '@/components/erp/date-picker'

/**
 * The English for `ContactOutcome`. The CODES come from `useCodes('contactOutcome')`.
 *
 * `GET /surveys/vocabulary` serves this list, so nothing here decides which
 * outcomes exist — this file decides only what each one is called and what it
 * means. An outcome the server adds appears in the picker on the next deploy of
 * the server, rendering as its raw code until somebody writes it a label, which
 * is a visible gap rather than a missing option.
 *
 * ## Why each one carries a sentence
 *
 * These codes are the AAPOR denominators. `NON_CONTACT` and `INELIGIBLE` look
 * interchangeable at the door and are not: a non-contact stays IN the eligible
 * base and drags the response rate down, an ineligible leaves it entirely and
 * drags the response rate UP. An interviewer who marks "wrong number" as a
 * non-contact understates the response rate; one who marks "nobody home" as
 * ineligible overstates it. Neither error is visible anywhere downstream — the
 * rate just comes out wrong, with no way to tell. So the meanings are keyed by
 * code and travel with the option, and an outcome with no meaning written for it
 * renders with no description rather than being dropped from the list.
 *
 * ## Why not `vocab.tsx`'s `dispositionLabel`
 *
 * Same codes, different reader. That map says "Completed interview" and "No
 * contact", which is right in an analyst's table and wrong on a radio the
 * interviewer reads at the door.
 */
const OUTCOME_LABEL: Record<string, string> = {
  COMPLETE: 'Complete',
  PARTIAL: 'Partial',
  REFUSAL: 'Refused',
  NON_CONTACT: 'Not reached',
  INELIGIBLE: 'Ineligible',
  LANGUAGE_BARRIER: 'Language barrier',
  CALLBACK: 'Callback agreed',
}

/** One line each, written for the person at the door, not for the analyst. */
const OUTCOME_MEANING: Record<string, string> = {
  COMPLETE: 'The interview happened and reached the end. This is the numerator of every '
    + 'response and cooperation rate.',
  PARTIAL: 'Started and broke off part-way. Counts as a response in RR3 and not in RR1 — '
    + 'which is the only difference between those two numbers.',
  REFUSAL: 'You reached an eligible person and they declined. Stays in the eligible base, '
    + 'so it lowers the response rate and is the whole of the refusal rate.',
  NON_CONTACT: 'Nobody answered — not home, phone unanswered, gate locked. They were eligible '
    + 'and you never spoke to them, so this lowers the contact rate but NOT the '
    + 'cooperation rate.',
  INELIGIBLE: 'Not a member of the population at all — wrong number, business premises, out of '
    + 'scope, under age. This leaves the eligible base entirely, so it does not count '
    + 'against you. Use it only when you know; guessing here inflates every rate.',
  LANGUAGE_BARRIER: 'Reached, eligible, and no language in common. Non-response, not '
    + 'ineligibility — they are in the population and we failed to interview them.',
  CALLBACK: 'Reached, and they asked you to come back. Nothing has finished happening yet, so '
    + 'it sits outside every numerator until the next attempt resolves it.',
}

/** A code this build has not seen renders as itself, never as the word "Unknown". */
export const outcomeLabel = (c?: string | null) =>
  !c ? '—' : (OUTCOME_LABEL[c] ?? c)

/**
 * One accent, and it is the refusal.
 *
 * A refusal is the only outcome a supervisor can act on the same day — a
 * non-contact is a callback and an ineligible is bookkeeping. Colouring every
 * outcome would be colouring none.
 */
export const outcomeTone = (c?: string | null): string | undefined =>
  c === 'REFUSAL' ? 'bg-amber-500/10 text-amber-600 border-amber-500/30' : undefined

/** `datetime-local` speaks local wall-clock with no zone; `attemptedAt` is an Instant. */
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/**
 * Recording one contact attempt.
 *
 * ## Why this dialog is the point of the whole screen
 *
 * `survey_contact_attempt` is the table RR1, RR3, COOP1, REF1 and CON1 are
 * computed from, and until this existed nothing in the application could write a
 * row to it. That is why `getDisposition` comes back with five nulls and a
 * suppression sentence: not because the fieldwork went badly, but because the
 * denominators were never populated. A poll can be at 96 of 120 collected and
 * still be unable to say whether the missing 24 refused, were never home, or were
 * never approached — three different problems with three different fixes.
 */
export function AttemptDialog({ open, surveyId, assignment, onClose, onSaved }: {
  open: boolean
  surveyId: string
  assignment: SurveyAssignmentDto
  onClose: () => void
  onSaved: () => void
}) {
  const outcomes = useCodes('contactOutcome')
  const [outcomeCode, setOutcomeCode] = useState('')
  const [attemptedAt, setAttemptedAt] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    // No default outcome. Pre-selecting COMPLETE would make the commonest
    // mis-record one click away, and pre-selecting anything else biases whichever
    // denominator it lands in.
    setOutcomeCode('')
    setAttemptedAt(toLocalInput(new Date()))
    setNote('')
    setError(null)
  }, [open, assignment.id])

  const revoked = assignment.statusCode === 'REVOKED'

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await recordAttempt(surveyId, assignment.id, {
        outcomeCode,
        // Omitted rather than sent as null when the field is cleared: the server
        // stamps `now` for a missing `attemptedAt`, which is the right answer for
        // an attempt being recorded as it happens.
        ...(fromLocalInput(attemptedAt) ? { attemptedAt: fromLocalInput(attemptedAt)! } : {}),
        unitId: assignment.scopeUnitId,
        note: note.trim() === '' ? undefined : note.trim(),
      })
      onSaved()
    } catch (e) {
      // `survey/unknown-outcome` lists the accepted codes; `survey/assignment-revoked`
      // explains that the existing attempts are kept. Both are better sentences
      // than anything this component could compose.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title="Record a contact attempt"
      description={`Attempt against ${assignment.mobilizerName ?? 'this assignment'}${
        assignment.scopeUnitName ? ` in ${assignment.scopeUnitName}` : ''}.`}
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          {/*
            Not gated on a client-side check of the outcome. `outcomeCode` is
            @NotBlank and the server owns the accepted list; an empty submit comes
            back naming all seven, which is more useful than a greyed button.
          */}
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Recording…' : 'Record attempt'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {revoked && (
          <Note tone="bad">
            This assignment is revoked. The server refuses a new attempt against it with
            `survey/assignment-revoked` — the attempts already recorded stay, because they are
            what was actually tried and the response rates read them.
          </Note>
        )}

        <fieldset className="space-y-1.5">
          <legend className="text-erp-xs font-medium text-muted-foreground mb-1.5">
            What happened <span className="text-red-600">*</span>
          </legend>
          {/*
            A radio list, not a <select>. The difference between NON_CONTACT and
            INELIGIBLE is the difference between two AAPOR denominators, and an
            explanation hidden behind an open dropdown is an explanation nobody
            reads at the door.
          */}
          {outcomes.loading && (
            <p className="text-erp-xs text-muted-foreground">Loading the outcome list…</p>
          )}
          {outcomes.unavailable && (
            // In visible text beside the empty group, not behind a title: an
            // outcome list that failed to load looks exactly like an outcome list
            // with nothing in it, and one of those readings ends with somebody
            // deciding no attempt can be recorded.
            <div className="space-y-1.5">
              <p className="text-erp-sm text-red-700">{CODE_LIST_UNAVAILABLE}</p>
              <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                onClick={() => outcomes.reload()}>
                Try again
              </button>
            </div>
          )}
          <div className="space-y-1">
            {outcomes.codes.map((code) => (
              <label
                key={code}
                className={`flex gap-2.5 rounded border p-2.5 cursor-pointer ${
                  outcomeCode === code
                    ? 'border-chocolate/50 bg-chocolate-soft/40'
                    : 'border-border hover:bg-muted/40'}`}
              >
                <input
                  type="radio"
                  name="outcomeCode"
                  className="mt-0.5 shrink-0"
                  value={code}
                  checked={outcomeCode === code}
                  onChange={() => setOutcomeCode(code)}
                />
                <span className="min-w-0">
                  <span className="block text-erp-sm font-medium">
                    {outcomeLabel(code)}
                    <span className="ml-1.5 font-mono text-erp-xs text-muted-foreground">
                      {code}
                    </span>
                  </span>
                  {/* An outcome this build has no sentence for is still offered —
                      the server says it is a valid disposition, and dropping it
                      would make a legal answer unrecordable. */}
                  {OUTCOME_MEANING[code] && (
                    <span className="block text-erp-xs text-muted-foreground mt-0.5">
                      {OUTCOME_MEANING[code]}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <Field label="When"
            hint="Leave it as now unless you are writing up an attempt from earlier.">
            <DateTimePicker value={attemptedAt} onChange={setAttemptedAt} className="h-9 w-full" />
          </Field>
          <Field label="Where"
            hint="The assignment's unit. An attempt is recorded against the area the interviewer holds.">
            <input className="erp-input h-9 w-full" readOnly
              value={assignment.scopeUnitName ?? assignment.scopeUnitId ?? 'no unit on this assignment'} />
          </Field>
        </div>

        <Field label="Note"
          hint="Up to 1,000 characters. What a supervisor would need to know to decide the next move.">
          <textarea className="erp-input w-full text-erp-sm" rows={2} maxLength={1000}
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Asked to come back after 6pm" />
        </Field>

        {outcomeCode && OUTCOME_MEANING[outcomeCode] && (
          <Note tone="info">
            <strong>{outcomeLabel(outcomeCode)}</strong> — {OUTCOME_MEANING[outcomeCode]}
          </Note>
        )}
      </div>
    </Modal>
  )
}
